import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ExternalLink } from 'lucide-react';

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

export default function ZohoDashboardFormCards({ forms }) {
  const navigate = useNavigate();

  if (!forms || forms.length === 0) {
    return null;
  }

  return (
    <div data-testid="form-dashboards-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Form Dashboards</h2>
        <span className="text-sm text-muted-foreground">{forms.length} forms</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {forms.map(f => (
          <Card
            key={f.id}
            className="cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/zoho-integration', { state: { selectedFormId: f.id } })}
            data-testid={`form-card-${f.id}`}
          >
            <CardContent className="pt-5 pb-4 space-y-3">
              <div className="flex items-start justify-between">
                <h3 className="font-medium text-sm truncate pr-2" title={f.form_name}>
                  {f.form_name}
                  <ExternalLink className="h-3 w-3 inline ml-1 text-muted-foreground" />
                </h3>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {f.category_name && (
                  <Badge variant="outline" className="text-xs">{f.category_name}</Badge>
                )}
                <Badge
                  variant={f.status === 'active' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {f.status}
                </Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Submissions</span>
                <span className="text-2xl font-bold">{f.filtered_submission_count ?? f.submission_count ?? 0}</span>
              </div>
              <div className="border-t pt-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>Last: {timeAgo(f.last_submission_at)}</span>
                <div className="flex items-center gap-3">
                  {f.primary_key_field && (
                    <span
                      className="text-blue-600 flex items-center gap-1 font-medium cursor-pointer hover:underline"
                      onClick={(e) => { e.stopPropagation(); navigate('/zoho-submissions/' + f.id); }}
                      data-testid={`form-card-dashboard-${f.id}`}
                    >
                      Dashboard <ArrowRight className="h-3 w-3" />
                    </span>
                  )}
                  <span className="text-primary flex items-center gap-1 font-medium">
                    Open <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
